import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, CircularProgress, Typography, IconButton, Tooltip, Paper, Grid, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Card, CardContent, Stack, useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { jwtDecode } from 'jwt-decode';
import { API, API_BASE_URL } from '../config/apiConfig';

// --- helpers ----------------------------------------------------
function parseJSON(str) {
  try { return JSON.parse(str); } catch { return null; }
}
function pick(obj, ...keys) {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return null;
}
function resolveUserIdFallbacks(propUserId) {
  if (propUserId != null) return Number(propUserId);
  const lsUserId = pick(localStorage, 'userId', 'UserID', 'userid');
  if (lsUserId) return Number(lsUserId);
  const user = parseJSON(localStorage.getItem('user')) || parseJSON(localStorage.getItem('profile'));
  const fromObj = pick(user || {}, 'userId', 'UserID', 'userid', 'id');
  if (fromObj) return Number(fromObj);
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  if (token) {
    try {
      const dec = jwtDecode(token);
      const fromJwt = pick(dec || {}, 'userId', 'UserID', 'userid', 'id', 'sub');
      if (fromJwt) return Number(fromJwt);
    } catch {
      // ignore
    }
  }
  return null;
}

function buildLedgerUrl(userId, fromDate, toDate) {
  let base;
  if (API?.tokenLedger) base = `${API.tokenLedger}/${userId}`;
  else if (API?.tokenLedgerByUser) base = `${API.tokenLedgerByUser}/${userId}`;
  else if (API?.earnings) base = `${API.earnings}/token-ledger/${userId}`;
  else base = `${API_BASE_URL}/api/earnings/token-ledger/${userId}`;
  if (fromDate && toDate) {
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`;
  }
  return base;
}

function n(x) { const v = Number(x); return Number.isFinite(v) ? v : 0; }
function fmtNumber(x) { return n(x).toLocaleString(undefined, { maximumFractionDigits: 2 }); }

// Daily token display: show actual value; color by range (0 red, 1-5 yellow, 6-8 blue, 9+ green)
function getTokenDisplayValue(tokens) { return n(tokens); }
function getTokenDisplayColor(tokens) {
  const t = n(tokens);
  return t === 0 ? '#dc2626' : t <= 5 ? '#eab308' : t <= 8 ? '#2563eb' : '#22c55e';
}

// Date formatting helpers
function formatDate(dateStr) {
  if (!dateStr) return '—';
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return String(dateStr);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(dateStr) {
  if (!dateStr) return '—';
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return String(dateStr);
  return dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return String(dateStr);
  return dt.toLocaleString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric',
    hour: '2-digit', 
    minute: '2-digit' 
  });
}

function getDateKey(dateStr) {
  if (!dateStr) return '';
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString().split('T')[0]; // YYYY-MM-DD
}

function getWeekKey(dateStr) {
  if (!dateStr) return '';
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return '';
  const weekStart = new Date(dt);
  weekStart.setDate(dt.getDate() - dt.getDay()); // Sunday
  return weekStart.toISOString().split('T')[0];
}

function formatWeekLabel(weekKey) {
  if (!weekKey) return '';
  const dt = new Date(weekKey);
  const weekEnd = new Date(dt);
  weekEnd.setDate(dt.getDate() + 6);
  return `${formatDate(weekKey)} - ${formatDate(weekEnd.toISOString())}`;
}

function isToday(dateStr) {
  if (!dateStr) return false;
  const dt = new Date(dateStr);
  const today = new Date();
  return dt.toDateString() === today.toDateString();
}

function isThisWeek(dateStr) {
  if (!dateStr) return false;
  const dt = new Date(dateStr);
  const today = new Date();
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay());
  return dt >= weekStart;
}

function isThisMonth(dateStr) {
  if (!dateStr) return false;
  const dt = new Date(dateStr);
  const today = new Date();
  return dt.getMonth() === today.getMonth() && dt.getFullYear() === today.getFullYear();
}

// --- component --------------------------------------------------
export default function TokenLedger({ userId }) {
  const theme = useTheme();
  const resolvedUserId = useMemo(() => resolveUserIdFallbacks(userId), [userId]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'table'

  const fetchLedger = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      if (!resolvedUserId) {
        throw new Error('Missing userId (not in props, localStorage, or JWT).');
      }

      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 30);
      const fromStr = fromDate.toISOString().split('T')[0];
      const toStr = toDate.toISOString().split('T')[0];
      const url = buildLedgerUrl(resolvedUserId, fromStr, toStr);

      const res = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const raw = await res.text();
      if (!res.ok) throw new Error(`${res.status}: ${raw.slice(0, 180)}`);

      let data;
      try { data = JSON.parse(raw); }
      catch { throw new Error('Non-JSON response from server. ' + raw.slice(0, 180)); }

      const items = Array.isArray(data) ? data : (data.ledger ?? data.data ?? []);
      // Sort by date descending (newest first)
      const sorted = Array.isArray(items) ? items.sort((a, b) => {
        const dateA = new Date(a?.CreatedAt ?? a?.CreatedAtUtc ?? 0);
        const dateB = new Date(b?.CreatedAt ?? b?.CreatedAtUtc ?? 0);
        return dateB - dateA;
      }) : [];
      // Only keep last 30 days
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      const last30 = sorted.filter((tx) => {
        const d = new Date(tx?.CreatedAt ?? tx?.CreatedAtUtc ?? 0);
        return !Number.isNaN(d.getTime()) && d >= cutoff;
      });
      setLedger(last30);
    } catch (e) {
      setErr(e.message || 'Failed to load token ledger');
      setLedger([]);
    } finally {
      setLoading(false);
    }
  }, [resolvedUserId]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  // Calculate summaries
  const summaries = useMemo(() => {
    const today = ledger.filter(tx => {
      const date = tx?.CreatedAt ?? tx?.CreatedAtUtc ?? '';
      return isToday(date);
    });
    const thisWeek = ledger.filter(tx => {
      const date = tx?.CreatedAt ?? tx?.CreatedAtUtc ?? '';
      return isThisWeek(date);
    });
    const thisMonth = ledger.filter(tx => {
      const date = tx?.CreatedAt ?? tx?.CreatedAtUtc ?? '';
      return isThisMonth(date);
    });

    const sum = (arr) => arr.reduce((acc, tx) => acc + n(tx?.TokensGenerated ?? tx?.Tokens ?? 0), 0);

    return {
      today: { count: today.length, tokens: sum(today) },
      thisWeek: { count: thisWeek.length, tokens: sum(thisWeek) },
      thisMonth: { count: thisMonth.length, tokens: sum(thisMonth) },
      allTime: { count: ledger.length, tokens: sum(ledger) },
    };
  }, [ledger]);

  // Last 30 days as YYYY-MM-DD (newest first)
  const last30DayKeys = useMemo(() => {
    const keys = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = getDateKey(d.toISOString());
      if (key) keys.push(key);
    }
    return keys;
  }, []);

  // Group transactions by day; fill last 30 days with 0 for missing days
  const groupedData = useMemo(() => {
    const grouped = {};
    ledger.forEach(tx => {
      const date = tx?.CreatedAt ?? tx?.CreatedAtUtc ?? '';
      const dayKey = getDateKey(date);
      if (!dayKey) return;
      if (!grouped[dayKey]) {
        grouped[dayKey] = { dayKey, transactions: [], dayTotal: 0 };
      }
      grouped[dayKey].transactions.push(tx);
      grouped[dayKey].dayTotal += n(tx?.TokensGenerated ?? tx?.Tokens ?? 0);
    });

    // One entry per day in last 30 days; 0 tokens if no data
    return last30DayKeys.map(dayKey => grouped[dayKey] ?? { dayKey, transactions: [], dayTotal: 0 });
  }, [ledger, last30DayKeys]);


  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box 
        display="flex" 
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }} 
        justifyContent="space-between" 
        mb={3} 
        gap={2}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700,
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
          }}
        >
          Engagement Detail
        </Typography>
        <Box 
          display="flex" 
          gap={1} 
          alignItems="center"
          width={{ xs: '100%', sm: 'auto' }}
          justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
        >
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
            sx={{ 
              flexShrink: 0,
              '& .MuiToggleButton-root': {
                px: { xs: 1, sm: 1.5 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }
            }}
          >
            <ToggleButton value="grouped">
              <ViewModuleIcon sx={{ mr: { xs: 0.25, sm: 0.5 }, fontSize: { xs: '16px', sm: '18px' } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Grouped</Box>
            </ToggleButton>
            <ToggleButton value="table">
              <ViewListIcon sx={{ mr: { xs: 0.25, sm: 0.5 }, fontSize: { xs: '16px', sm: '18px' } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Table</Box>
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton 
              onClick={fetchLedger} 
              size="small"
              sx={{ 
                padding: { xs: '6px', sm: '8px' }
              }}
            >
              <RefreshIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Cards - one row, 25% each */}
      {!loading && !err && ledger.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1, sm: 2 },
            mb: 3,
            width: '100%',
            flexWrap: 'nowrap',
            overflowX: 'auto',
          }}
        >
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <Card 
              elevation={0} 
              sx={{ 
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                color: 'white',
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <TodayIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}
                  >
                    {fmtNumber(summaries.today.tokens)}
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    opacity: 0.9,
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}
                >
                  Today
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <Card 
              elevation={0} 
              sx={{ 
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
                color: 'white',
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <DateRangeIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}
                  >
                    {fmtNumber(summaries.thisWeek.tokens)}
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    opacity: 0.9,
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}
                >
                  This Week
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <Card 
              elevation={0} 
              sx={{ 
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
                color: 'white',
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <CalendarMonthIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}
                  >
                    {fmtNumber(summaries.thisMonth.tokens)}
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    opacity: 0.9,
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}
                >
                  This Month
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <Card 
              elevation={0} 
              sx={{ 
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', 
                color: 'white',
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <TrendingUpIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}
                  >
                    {fmtNumber(summaries.allTime.tokens)}
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    opacity: 0.9,
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}
                >
                  All Time
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box display="flex" alignItems="center" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {!loading && err && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'error.light',
            borderRadius: 2,
            mb: 2,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            color: 'error.main',
          }}
        >
          <ErrorOutlineIcon fontSize="small" />
          <Typography variant="body2">{err}</Typography>
        </Paper>
      )}

      {/* Empty State */}
      {!loading && !err && ledger.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }}>
          <Typography variant="body1" color="text.secondary">
            No transactions found.
          </Typography>
        </Paper>
      )}

      {/* Grouped View - Daily Totals Only */}
      {!loading && !err && ledger.length > 0 && viewMode === 'grouped' && (
        <Stack spacing={{ xs: 1.5, sm: 2 }}>
          {groupedData.map((day) => (
            <Paper 
              key={day.dayKey} 
              elevation={0} 
              sx={{ 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: { xs: 2, sm: 3 }, 
                overflow: 'hidden',
                p: { xs: 1.5, sm: 2 },
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'action.hover' },
                transition: 'background-color 0.2s',
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', sm: '1.1rem' }
                }}
              >
                {formatDate(day.dayKey)}
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  color: getTokenDisplayColor(day.dayTotal),
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  alignSelf: 'auto'
                }}
              >
                {fmtNumber(getTokenDisplayValue(day.dayTotal))}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Table View - Daily Totals Only */}
      {!loading && !err && ledger.length > 0 && viewMode === 'table' && (
        <TableContainer 
          component={Paper} 
          elevation={0} 
          sx={{ 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: { xs: 2, sm: 3 },
            overflowX: 'auto'
          }}
        >
          <Table sx={{ minWidth: { xs: 300, sm: 500 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    py: { xs: 1, sm: 1.5 }
                  }}
                >
                  Date
                </TableCell>
                <TableCell 
                  align="right" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    py: { xs: 1, sm: 1.5 }
                  }}
                >
                  Total Tokens
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedData.map((day, idx) => (
                <TableRow key={idx} hover>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      py: { xs: 1.25, sm: 1.5 }
                    }}
                  >
                    {formatDate(day.dayKey)}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      fontWeight: 600, 
                      color: getTokenDisplayColor(day.dayTotal),
                      fontSize: { xs: '0.85rem', sm: '0.875rem' },
                      py: { xs: 1.25, sm: 1.5 }
                    }}
                  >
                    {fmtNumber(getTokenDisplayValue(day.dayTotal))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
